import { PrimitiveShape, degToRad, PrimitiveMeshMaterial } from "flavr-common";
import { Color, Quaternion, Vector3, Euler } from "flavr-math";
import { Renderable } from "../renderable/Renderable";
const tmpVector3_1 = new Vector3();
const tmpVector3_2 = new Vector3();
const tmpQuaternion_1 = new Quaternion();
const tmpColor = new Color();
const tmpEuler = new Euler();
const MAX_SEGMENT_ARC_LENGTH = degToRad(30);
export class ArcRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.type = ArcRenderable.typestatic;
        this.material = PrimitiveMeshMaterial.SIMPLE_SHADING;
        this.segments = [];
    }
    destroy() {
        for (const it of this.segments)
            it.instance.destroy();
        this.segments = [];
    }
    setVisible(visible) {
        for (const it of this.segments)
            it.instance.setVisible(visible);
    }
    getCenter(center) {
        if (this.arcLength) {
            const cos = Math.cos(this.arcLength / 2);
            const r = 0.25 * (1 + cos);
            center.set(0, 0, r).multiply(this.owner.worldScale).applyQuaternion(this.owner.worldOrientation).add(this.owner.worldPosition);
        }
        else {
            center.copy(this.owner.worldPosition);
        }
        return center;
    }
    updateFacet(facet) {
        tmpColor.set(facet.color);
        this.arcLength = facet.arcLength;
        if (this.material !== facet.material)
            this.destroy();
        const numSegments = Math.ceil(facet.arcLength / MAX_SEGMENT_ARC_LENGTH);
        while (this.segments.length > numSegments) {
            const last = this.segments.pop();
            last && last.instance.destroy();
        }
        const segmentAngle = facet.arcLength / numSegments;
        const d = 0.5 * Math.sin(segmentAngle / 2);
        const r = Math.sqrt(0.25 - d * d);
        let alpha = -0.5 * segmentAngle * (numSegments - 1);
        for (let i = 0; i < numSegments; ++i) {
            this.createSegment(i, alpha, facet.material, d, r);
            alpha += segmentAngle;
        }
    }
    createSegment(index, alpha, material, d, r) {
        if (index >= this.segments.length) {
            this.segments.push({
                instance: this.owner.renderer.createInstancedPrimitiveMesh(this.owner.modelUID, PrimitiveShape.Prism, material),
                position: new Vector3(),
                orientation: new Quaternion(),
                scale: new Vector3()
            });
        }
        const segment = this.segments[index];
        segment.position.set(0.5 * r * Math.sin(alpha), 0, 0.5 * r * Math.cos(alpha));
        segment.scale.set(2 * d, r, 1);
        segment.orientation.setFromEuler(tmpEuler.set(degToRad(-90), alpha, 0, "ZYX"));
        segment.instance.setColor(tmpColor);
    }
    updateTransform() {
        const matrixWorld = this.owner.matrixWorld;
        const worldOrientation = this.owner.worldOrientation;
        const worldScale = this.owner.worldScale;
        for (const it of this.segments) {
            const pos = tmpVector3_1.copy(it.position).applyMatrix4(matrixWorld);
            const ori = tmpQuaternion_1.copy(it.orientation).premultiply(worldOrientation);
            const scale = tmpVector3_2.copy(it.scale);
            scale.x *= worldScale.x;
            scale.y *= worldScale.z;
            scale.z *= worldScale.y;
            it.instance.setPosition(pos);
            it.instance.setOrientation(ori);
            it.instance.setScale(scale);
        }
    }
}
ArcRenderable.typestatic = "Arc";
